import type { FileChangeEvent } from "schemas/generated/agentConnection";

import picomatch from "picomatch";
import { filesChangedMessageSchema } from "schemas/agentConnection";

export type FilesChangedHandler = (events: FileChangeEvent[]) => void;

export interface FilesChangedSubscribeOptions {
  // Inclusion globs matched against an event's namespace-relative `path`
  // (e.g. `.plans/**`, `.opencode/skills/**`). Defaults to "**/*".
  paths?: string[];
  // Exclusion globs applied after `paths`. Defaults to none.
  exclude?: string[];
  // Minimum delay between deliveries to *this* listener. Coalesces bursts of
  // events (e.g. a multi-file upload) into a single delivery, last-write-wins
  // per fileId.
  throttleMs?: number;
}

interface OnMessage {
  (handler: (message: Record<string, unknown>) => void): () => void;
}

function compileMatcher({
  paths,
  exclude,
}: {
  paths?: string[];
  exclude?: string[];
}): (path: string) => boolean {
  // `dot: true` so `**` traverses dot-directories — the Drive paths consumers
  // watch (`.plans/**`, `.opencode/skills/**`) live under dotfolders, and the
  // default `**/*` must reach them too.
  const isIncluded = picomatch(paths ?? ["**/*"], { dot: true });
  const isExcluded =
    exclude && exclude.length > 0
      ? picomatch(exclude, { dot: true })
      : () => false;
  return (path) => isIncluded(path) && !isExcluded(path);
}

// A rename/move out of a watched directory must still notify the *old*
// subscriber, so an `updated` event matches on either side of the move.
function eventMatches(
  matches: (path: string) => boolean,
  event: FileChangeEvent,
): boolean {
  if (matches(event.path)) {
    return true;
  }
  return event.op === "updated" && matches(event.oldPath);
}

export function subscribeToFilesChanged({
  onMessage,
  options,
  handler,
}: {
  onMessage: OnMessage;
  options: FilesChangedSubscribeOptions;
  handler: FilesChangedHandler;
}): () => void {
  const matches = compileMatcher(options);
  const throttleMs = options.throttleMs ?? 0;

  // Highest `version` (the file's updatedAt) already accepted per fileId. An
  // event at or below it is a duplicate or reconnect replay we already hold, so
  // we drop it. Deletes are terminal and bypass this, clearing the mark so a
  // later recreate is delivered.
  const lastVersionByFileId = new Map<string, number>();

  // Last-write-wins per fileId: a burst that touches the same file repeatedly
  // collapses to its final state before delivery.
  let pending = new Map<string, FileChangeEvent>();
  let isDelivering = false;
  let lastDeliveredAt = 0;
  let disposed = false;

  async function deliverPending() {
    if (isDelivering) {
      return;
    }
    isDelivering = true;
    try {
      while (!disposed && pending.size > 0) {
        const wait = lastDeliveredAt + throttleMs - Date.now();
        if (wait > 0) {
          await new Promise((resolve) => setTimeout(resolve, wait));
          if (disposed) {
            return;
          }
        }
        const events = [...pending.values()];
        pending = new Map();
        lastDeliveredAt = Date.now();
        handler(events);
      }
    } finally {
      isDelivering = false;
    }
  }

  const unsubscribe = onMessage((rawMessage) => {
    if (rawMessage.type !== "files-changed") {
      return;
    }
    const parsed = filesChangedMessageSchema.safeParse(rawMessage);
    if (!parsed.success) {
      return;
    }
    for (const event of parsed.data.events) {
      if (!eventMatches(matches, event)) {
        continue;
      }
      if (event.op === "deleted") {
        lastVersionByFileId.delete(event.fileId);
        pending.set(event.fileId, event);
        continue;
      }
      // An older server (mid rolling deploy) may omit `version`; deliver those
      // without deduping rather than dropping realtime entirely.
      if (event.version !== undefined) {
        const lastVersion = lastVersionByFileId.get(event.fileId);
        if (lastVersion !== undefined && event.version <= lastVersion) {
          continue;
        }
        lastVersionByFileId.set(event.fileId, event.version);
      }
      pending.set(event.fileId, event);
    }
    if (pending.size > 0) {
      void deliverPending();
    }
  });

  return () => {
    disposed = true;
    unsubscribe();
  };
}
